/**
 * IDFY API client. Auth endpoints proxy to roots-mms via /mms.
 * Persona/interaction endpoints go to idfy-service via /api.
 *
 * Path rewriting:
 *   In dev, Vite's server.proxy in vite.config.ts maps `/mms`, `/api`,
 *   `/oauth2`, `/login/oauth2` to the right backends, so this client uses
 *   relative paths everywhere. In production (Cloudflare Pages) there is
 *   no proxy — `resolveUrl` prefixes those paths with the absolute backend
 *   URLs baked in at build time via VITE_MMS_URL / VITE_API_URL. When the
 *   env vars aren't set we fall back to relative, which keeps `npm run dev`
 *   working unchanged.
 */

const MMS_URL = (import.meta.env.VITE_MMS_URL || '').replace(/\/+$/, '')
const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')

function resolveUrl(path: string): string {
  if (path.startsWith('/mms/')) {
    // Vite's `/mms` rewrite strips `/mms` and prepends `/api` before
    // forwarding to mms-service. Replicate that here.
    return MMS_URL ? `${MMS_URL}/api/${path.slice('/mms/'.length)}` : path
  }
  if (path.startsWith('/api/')) {
    return API_URL ? `${API_URL}${path}` : path
  }
  if (path.startsWith('/oauth2/') || path.startsWith('/login/oauth2/')) {
    return MMS_URL ? `${MMS_URL}${path}` : path
  }
  return path
}

// ── Token store ────────────────────────────────────────────────────────
let accessToken: string | null = null
let refreshToken: string | null = null

const AUTH_KEY = 'platform_auth'

try {
  const saved = JSON.parse(localStorage.getItem(AUTH_KEY) || 'null')
  if (saved) { accessToken = saved.accessToken; refreshToken = saved.refreshToken }
} catch (_) {}

export function setAuthTokens(t: { accessToken?: string; token?: string; refreshToken?: string }) {
  accessToken = t.accessToken || t.token || null
  refreshToken = t.refreshToken || null
  try { localStorage.setItem(AUTH_KEY, JSON.stringify({ accessToken, refreshToken })) } catch (_) {}
}

export function clearAuthTokens() {
  accessToken = null; refreshToken = null
  try { localStorage.removeItem(AUTH_KEY) } catch (_) {}
}

export function getAccessToken() { return accessToken }

// ── Fetch helpers ──────────────────────────────────────────────────────

interface FetchOpts extends RequestInit {
  noAuth?: boolean
  _retry?: boolean
}

let refreshInFlight: Promise<unknown> | null = null

async function doRefresh() {
  if (refreshInFlight) return refreshInFlight
  if (!refreshToken) throw new Error('No refresh token')
  refreshInFlight = fetch(resolveUrl(`/mms/auth/refresh?refreshToken=${encodeURIComponent(refreshToken)}`), {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
  }).then(async (res) => {
    const text = await res.text()
    let data: { accessToken?: string; token?: string; refreshToken?: string } | null = null
    try { data = text ? JSON.parse(text) : null } catch (_) {}
    if (!res.ok) throw new Error('Refresh failed')
    if (!data || !(data.accessToken || data.token)) throw new Error('Invalid refresh')
    setAuthTokens({ accessToken: data.accessToken || data.token, refreshToken: data.refreshToken })
    return data
  }).finally(() => { refreshInFlight = null })
  return refreshInFlight
}

function generateTraceId(): string {
  return 'idfy-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36)
}

async function fetchJson<T = unknown>(url: string, opts: FetchOpts = {}): Promise<T> {
  const headers: Record<string, string> = opts.headers ? { ...(opts.headers as Record<string, string>) } : {}
  if (opts.body && !headers['Content-Type'] && !(opts.body instanceof FormData)) headers['Content-Type'] = 'application/json'
  if (!opts.noAuth && accessToken && !headers['Authorization']) headers['Authorization'] = `Bearer ${accessToken}`
  if (!headers['X-Trace-Id']) headers['X-Trace-Id'] = generateTraceId()
  const res = await fetch(resolveUrl(url), { ...opts, credentials: 'include', headers })
  const text = await res.text()
  let data: unknown = null
  try { data = text ? JSON.parse(text) : null } catch (_) { data = text }
  if (!res.ok) {
    const d = data as Record<string, string> | null
    const err: Error & { status?: number; data?: unknown } = new Error((d && (d['message'] || d['error'])) || res.statusText)
    err.status = res.status; err.data = data
    // Auto-refresh on 401 (once)
    if (err.status === 401 && refreshToken && !opts._retry) {
      try {
        await doRefresh()
        return fetchJson<T>(url, { ...opts, _retry: true })
      } catch (_) { clearAuthTokens() }
    }
    throw err
  }
  return data as T
}

// ── Auth (via roots-mms proxy) ─────────────────────────────────────────

export interface AuthUser {
  id: string; username: string; email: string; firstName?: string; lastName?: string
  roles?: string[]; provider?: string; hasPassword?: boolean; emailVerified?: boolean; twoFactorEnabled?: boolean
}

export function signin(creds: { username: string; password: string; totpCode?: string }) {
  const body: Record<string, string> = { username: creds.username, password: creds.password }
  if (creds.totpCode) body.totpCode = creds.totpCode
  return fetchJson<{ accessToken?: string; token?: string; refreshToken?: string }>('/mms/auth/signin', {
    method: 'POST', body: JSON.stringify(body), noAuth: true,
  } as RequestInit & { noAuth: boolean }).then((r) => { if (r.accessToken || r.token) setAuthTokens(r); return r })
}

export function signup(data: Record<string, unknown>) {
  return fetchJson('/mms/auth/signup', { method: 'POST', body: JSON.stringify(data), noAuth: true } as RequestInit & { noAuth: boolean })
}

export function validateInviteCode(code: string) {
  return fetchJson<{ valid: boolean; description?: string }>('/mms/auth/validate-invite', {
    method: 'POST', body: JSON.stringify({ code }), noAuth: true,
  } as RequestInit & { noAuth: boolean })
}

export function getSignupConfig() {
  return fetchJson<{ inviteRequired: boolean }>('/mms/auth/signup-config', {
    method: 'GET', noAuth: true,
  } as RequestInit & { noAuth: boolean })
}

export function forgotPassword(email: string) {
  return fetchJson('/mms/auth/forgot-password', {
    method: 'POST', body: JSON.stringify({ email }), noAuth: true,
  } as RequestInit & { noAuth: boolean })
}

export function resetPassword(token: string, newPassword: string) {
  return fetchJson('/mms/auth/reset-password', {
    method: 'POST', body: JSON.stringify({ token, newPassword }), noAuth: true,
  } as RequestInit & { noAuth: boolean })
}

export function verifyEmail(token: string) {
  return fetchJson(`/mms/auth/verify-email?token=${encodeURIComponent(token)}`, {
    method: 'GET', noAuth: true,
  } as RequestInit & { noAuth: boolean })
}

export function signout() {
  return fetchJson('/mms/auth/signout', { method: 'POST' }).catch(() => {}).finally(() => clearAuthTokens())
}

export function me() { return fetchJson<AuthUser>('/mms/users/me') }

export interface FeatureRecord {
  id: string; code: string; name: string; description?: string; category?: string; icon?: string; enabled?: boolean
}

export function getMyFeatures() { return fetchJson<FeatureRecord[]>('/mms/features/mine') }

// ── Account management ────────────────────────────────────────────────

export function updateMe(payload: Record<string, unknown>) {
  return fetchJson<AuthUser>('/mms/users/me', { method: 'PUT', body: JSON.stringify(payload) })
}

export function changePassword(payload: { currentPassword: string; newPassword: string }) {
  return fetchJson('/mms/users/me/password', { method: 'PUT', body: JSON.stringify(payload) })
}

export function disconnectProvider(newPassword: string) {
  return fetchJson<AuthUser>('/mms/users/me/disconnect-provider', { method: 'POST', body: JSON.stringify({ newPassword }) })
}

export function uploadAvatar(file: File) {
  const form = new FormData()
  form.append('file', file)
  return fetchJson('/mms/users/me/avatar', { method: 'POST', body: form })
}

export async function getMyAvatarBlob(): Promise<Blob> {
  const headers: Record<string, string> = {}
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  // Same path-resolution rule as fetchJson — raw fetch() needs the helper
  // because in production /mms/* hits Cloudflare Pages (404) instead of
  // mms-service via Vite proxy (which only exists in dev).
  const res = await fetch(resolveUrl('/mms/users/me/avatar'), { method: 'GET', headers, credentials: 'include' })
  if (!res.ok) throw new Error('No avatar')
  return res.blob()
}

export interface PrefsRecord {
  theme?: string; language?: string; country?: string; timezone?: string
  emailNotifications?: boolean; navbarDisplay?: string
}

export function getPreferences() { return fetchJson<PrefsRecord>('/mms/user/preferences') }
export function updatePreferences(payload: Record<string, unknown>) { return fetchJson<PrefsRecord>('/mms/user/preferences', { method: 'POST', body: JSON.stringify(payload) }) }

// ── Locale options (from MMS backend) ─────────────────────────────
export interface LocaleOption { id: string; code: string; label: string }
export function getLanguages() { return fetchJson<LocaleOption[]>('/mms/locale/languages', { noAuth: true } as RequestInit & { noAuth: boolean }) }
export function getCountries() { return fetchJson<LocaleOption[]>('/mms/locale/countries', { noAuth: true } as RequestInit & { noAuth: boolean }) }
export function getTimezones() { return fetchJson<LocaleOption[]>('/mms/locale/timezones', { noAuth: true } as RequestInit & { noAuth: boolean }) }

// ── 2FA ───────────────────────────────────────────────────────────────

export function twoFactorSetup() { return fetchJson<{ secret: string; otpauthUri: string }>('/mms/users/me/2fa/setup', { method: 'POST' }) }
export function twoFactorEnable(code: string) { return fetchJson<{ recoveryCodes: string[] }>('/mms/users/me/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) }) }
export function twoFactorDisable(currentPasswordOrCode: string) { return fetchJson('/mms/users/me/2fa/disable', { method: 'POST', body: JSON.stringify({ currentPasswordOrCode }) }) }

// ── Persona API ────────────────────────────────────────────────────────

export interface PersonaData {
  id?: string; handle?: string; name?: string; type?: string
  publicBio?: string; description?: string; knowledgeBase?: string
  tone?: string; boundaries?: string; canHelpWith?: string[]; cannotHelpWith?: string[]
  ownerDisplayName?: string; ownerLogoUrl?: string; personaPhoto?: string
  strictness?: string; aiInstructions?: string; refusalMessage?: string
  allowGeneralTasks?: boolean; responseLength?: string; preferredModel?: string
  disabledRules?: string[]
  isActive?: boolean; isPaused?: boolean; consentedAt?: string
}

export function getMyPersona() { return fetchJson<PersonaData>('/api/persona/mine') }
export function listMyPersonas() { return fetchJson<PersonaData[]>('/api/persona/list') }
export function createPersona(data: PersonaData) { return fetchJson<PersonaData>('/api/persona', { method: 'POST', body: JSON.stringify(data) }) }
export function updatePersona(id: string, data: PersonaData) { return fetchJson<PersonaData>(`/api/persona/${id}`, { method: 'PUT', body: JSON.stringify(data) }) }
export function consentPersona(id: string) { return fetchJson<PersonaData>(`/api/persona/${id}/consent`, { method: 'POST' }) }
export function pausePersona(id: string) { return fetchJson<PersonaData>(`/api/persona/${id}/pause`, { method: 'POST' }) }
export function resumePersona(id: string) { return fetchJson<PersonaData>(`/api/persona/${id}/resume`, { method: 'POST' }) }
export function deletePersona(id: string) { return fetchJson(`/api/persona/${id}`, { method: 'DELETE' }) }
export function checkHandle(handle: string) { return fetchJson<{ available: boolean; valid: boolean; reason?: string }>(`/api/persona/handle-available/${handle}`) }
export function changeHandle(personaId: string, handle: string) { return fetchJson<PersonaData>(`/api/persona/${personaId}/handle`, { method: 'PUT', body: JSON.stringify({ handle }) }) }
export function getPersonaActivity(id: string) { return fetchJson<ActivitySummary>(`/api/persona/${id}/activity`) }

// ── Knowledge sources (phase 1: file upload) ─────────────────────────

export interface KnowledgeSourceView {
  id: string
  type: 'FILE' | 'URL'
  filename: string
  mimeType?: string
  sizeBytes: number
  status: 'PROCESSING' | 'READY' | 'FAILED'
  errorMessage?: string
  extractedBytes: number
  truncated: boolean
  createdAt?: string
  updatedAt?: string
}

export interface KnowledgeSourcesList {
  sources: KnowledgeSourceView[]
  totalBytes: number
  maxSources: number
  maxBytesPerFile: number
  maxTotalBytes: number
  featureEnabled: boolean
}

export function listKnowledgeSources(personaId: string) {
  return fetchJson<KnowledgeSourcesList>(`/api/persona/${personaId}/knowledge-sources`)
}

export function uploadKnowledgeSource(personaId: string, file: File) {
  const form = new FormData()
  form.append('file', file)
  return fetchJson<KnowledgeSourceView>(`/api/persona/${personaId}/knowledge-sources`, {
    method: 'POST',
    body: form,
  })
}

export function deleteKnowledgeSource(personaId: string, sourceId: string) {
  return fetchJson(`/api/persona/${personaId}/knowledge-sources/${sourceId}`, { method: 'DELETE' })
}

export function previewKnowledgeSource(personaId: string, sourceId: string) {
  return fetchJson<{ id: string; filename: string; extractedText: string; extractedBytes: number; truncated: boolean }>(
    `/api/persona/${personaId}/knowledge-sources/${sourceId}/preview`
  )
}

export interface EnforcementRule { id: string; text: string; tier: string; category: string; enabledByDefault: boolean }
export function getOptionalRules() { return fetchJson<EnforcementRule[]>('/mms/enforcement-rules/optional', { noAuth: true } as RequestInit & { noAuth: boolean }) }

export interface ModelOption {
  id: string; label: string; provider: string; description: string
  /** True when the caller's tier does not entitle them to this model. */
  locked?: boolean
  /** One of STANDARD, ADVANCED, PREMIUM. */
  tier?: string
}
export function getAvailableModels() { return fetchJson<ModelOption[]>('/api/persona/models') }

// ── Entitlements ──────────────────────────────────────────────────────
export interface ResolvedEntitlements {
  userId: string
  categoryCode?: string
  tierCode?: string
  categoryEnabled?: boolean
  tierEnabled?: boolean
  entitlements: Record<string, string>
}

/**
 * Resolved entitlements for the signed-in member. Prefer the idfy-service
 * passthrough (same JWT) so the UI talks to a single backend for persona
 * concerns — MMS still provides the data but the proxy lets us keep the
 * dev proxy contract narrow.
 */
export function getMyEntitlements() { return fetchJson<ResolvedEntitlements>('/api/persona/entitlements') }

// ── Membership Governance (categories + tiers) ──────────────────────────
export interface TierRecord {
  id: string
  categoryCode: string
  tierCode: string
  displayName?: string
  description?: string
  enabled: boolean
  sortOrder: number
  system: boolean
}

export interface CategoryRecord {
  id: string
  code: string
  displayName?: string
  description?: string
  enabled: boolean
  sortOrder: number
  system: boolean
  tiers?: TierRecord[]
}

export function listCategories() {
  return fetchJson<CategoryRecord[]>('/mms/governance/categories')
}

/** Self-serve tier selection for the signed-in user. Creates a member record if missing. */
export function selectMyTier(categoryCode: string, tierCode: string) {
  return fetchJson('/mms/members/me/tier', {
    method: 'PUT',
    body: JSON.stringify({ categoryCode, tierCode }),
  })
}

export function entitlementBool(e: ResolvedEntitlements | null | undefined, key: string, fallback = false): boolean {
  if (!e || !e.entitlements) return fallback
  const v = e.entitlements[key]
  if (v == null) return fallback
  return v === 'true' || v === '1'
}

export function entitlementInt(e: ResolvedEntitlements | null | undefined, key: string, fallback = 0): number {
  if (!e || !e.entitlements) return fallback
  const v = e.entitlements[key]
  if (!v) return fallback
  const n = parseInt(v, 10)
  return isNaN(n) ? fallback : n
}

export interface ChatSession {
  sessionId: string
  lastMessage: string
  lastActivity: string
  messageCount: number
  inviteToken?: string
}

export interface Page<T> {
  content: T[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

export function getChatSessions(personaId: string, opts: { page?: number; size?: number } = {}) {
  const params = new URLSearchParams()
  params.set('page', String(opts.page ?? 0))
  params.set('size', String(opts.size ?? 10))
  return fetchJson<Page<ChatSession>>(`/api/persona/${personaId}/sessions?${params}`)
}
export function hideChatSession(personaId: string, sessionId: string) { return fetchJson(`/api/persona/${personaId}/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' }) }
export function summarizeSession(personaId: string, sessionId: string) { return fetchJson<{ summary: string }>(`/api/persona/${personaId}/sessions/${encodeURIComponent(sessionId)}/summarize`, { method: 'POST' }) }

// ── Invite Tokens ─────────────────────────────────────────────────────

export interface InviteToken {
  id: string; personaId: string; token: string; label?: string
  active: boolean; createdAt: string; expiresAt?: string
  lastUsedAt?: string; usageCount: number
}

export function generateInviteToken(personaId: string, label?: string, expiresAt?: string) {
  const body: Record<string, unknown> = {}
  if (label) body.label = label
  if (expiresAt) body.expiresAt = expiresAt
  return fetchJson<InviteToken>(`/api/persona/${personaId}/tokens`, { method: 'POST', body: JSON.stringify(body) })
}

export type InviteTokenStatus = 'all' | 'active' | 'revoked'
export interface InviteTokenPage extends Page<InviteToken> {
  activeCount: number
  revokedCount: number
}

export function listInviteTokens(
  personaId: string,
  opts: { status?: InviteTokenStatus; page?: number; size?: number } = {},
) {
  const params = new URLSearchParams()
  if (opts.status) params.set('status', opts.status)
  params.set('page', String(opts.page ?? 0))
  params.set('size', String(opts.size ?? 10))
  return fetchJson<InviteTokenPage>(`/api/persona/${personaId}/tokens?${params}`)
}

export function revokeInviteToken(personaId: string, tokenId: string) {
  return fetchJson<InviteToken>(`/api/persona/${personaId}/tokens/${tokenId}/revoke`, { method: 'PUT' })
}

export function deleteInviteToken(personaId: string, tokenId: string) {
  return fetchJson(`/api/persona/${personaId}/tokens/${tokenId}`, { method: 'DELETE' })
}

// ── Public persona (token-protected) ──────────────────────────────────

export function getPublicPersona(handle: string, token: string) {
  return fetchJson<PersonaData>(`/api/persona/public/${handle}?t=${encodeURIComponent(token)}`, { noAuth: true } as RequestInit & { noAuth: boolean })
}

export function interactWithPersona(handle: string, message: string, sessionId: string, token: string) {
  return fetchJson<{ response: string; status: string }>(`/api/interact/${handle}?t=${encodeURIComponent(token)}`, {
    method: 'POST',
    body: JSON.stringify({ message }),
    headers: { 'X-Session-Id': sessionId, 'Content-Type': 'application/json' },
    noAuth: true,
  } as RequestInit & { noAuth: boolean })
}

export interface ChatMessage {
  id: string; visitorInput: string; modelOutput: string
  responseStatus: string; createdAt: string
}

export function getChatHistory(handle: string, sessionId: string, token: string) {
  return fetchJson<{ content: ChatMessage[] } | ChatMessage[]>(`/api/interact/${handle}/history?t=${encodeURIComponent(token)}&sessionId=${encodeURIComponent(sessionId)}`, {
    noAuth: true,
  } as RequestInit & { noAuth: boolean }).then((res) => Array.isArray(res) ? res : res?.content || [])
}

// ── Types ──────────────────────────────────────────────────────────────

export interface ActivitySummary {
  totalVisits: number; totalInteractions: number
  allowedResponses: number; refusedResponses: number; blockedResponses: number
  lastActivity?: string; recentEvents?: { eventType: string; createdAt: string }[]
}
